import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Renders a professional specifications table for a listing's custom_fields,
 * looking up the matching schema so we can show the proper label and option text.
 * Fields with no value are skipped.
 */
export default function SpecsTable({ listing }) {
  const { language } = useLanguage();
  const [fields, setFields] = useState([]);

  useEffect(() => {
    if (!listing || !listing.category) return;
    const params = new URLSearchParams({ category: listing.category });

    let sub = '';
    let typ = '';
    if (listing.category === 'event') {
      sub = listing.event_subcategory;
      typ = listing.event_type;
    } else if (listing.category === 'service') {
      sub = listing.service_subcategory;
      typ = listing.service_type;
    } else if (listing.category === 'shop') {
      sub = listing.subcategory;
      typ = listing.product_type;
    } else if (listing.category === 'business') {
      sub = listing.business_type;
    }
    if (sub) params.set('subcategory', sub);
    if (typ) params.set('type', typ);

    axios.get(`${API}/field-schema?${params.toString()}`)
      .then(({ data }) => setFields(data.fields || []))
      .catch(() => setFields([]));
  }, [listing]);

  const customFields = listing?.custom_fields || {};
  if (fields.length === 0 || Object.keys(customFields).length === 0) return null;

  // Helper to format a field value for display based on its schema type.
  const formatValue = (field, val) => {
    if (val === undefined || val === null || val === '') return null;
    if (Array.isArray(val) && val.length === 0) return null;

    const lbl = (o) => (language === 'lv' ? o.lv : o.en);

    if (field.type === 'checkbox') {
      return val ? (
        <span className="inline-flex items-center gap-1 text-green-700">
          <Check className="w-4 h-4" />
          {language === 'lv' ? 'Jā' : 'Yes'}
        </span>
      ) : null;
    }
    if (field.type === 'select') {
      const opt = (field.options || []).find((o) => o.value === val);
      return opt ? lbl(opt) : String(val);
    }
    if (field.type === 'multiselect') {
      const sel = (field.options || []).filter((o) => val.includes(o.value));
      if (sel.length === 0) return null;
      return (
        <div className="flex flex-wrap gap-1.5">
          {sel.map((o) => (
            <span key={o.value} className="text-xs px-2 py-0.5 rounded-full bg-[#F3F2EE] text-[#121212]">
              {lbl(o)}
            </span>
          ))}
        </div>
      );
    }
    if (field.type === 'url') {
      return (
        <a href={val} target="_blank" rel="noopener noreferrer" className="text-[#D97736] hover:underline break-all">
          {val}
        </a>
      );
    }
    return String(val);
  };

  // Pre-compute rows so we can skip the whole block if no field has a value.
  const rows = fields.map((f) => {
    const raw = customFields[f.key];
    const display = formatValue(f, raw);
    if (display === null || display === undefined) return null;
    return { field: f, display };
  }).filter(Boolean);

  if (rows.length === 0) return null;

  return (
    <div className="mt-6 border border-[#E5E4E0] rounded-xl overflow-hidden" data-testid="specs-table">
      <div className="px-4 py-3 bg-[#F3F2EE] border-b border-[#E5E4E0]">
        <h3 className="font-outfit font-semibold text-[#121212]">
          {language === 'lv' ? 'Specifikācijas' : 'Specifications'}
        </h3>
      </div>
      <dl className="divide-y divide-[#F3F2EE]">
        {rows.map(({ field, display }) => (
          <div key={field.key} className="grid grid-cols-1 sm:grid-cols-3 gap-2 px-4 py-3">
            <dt className="text-sm text-[#5C5C5C]">
              {language === 'lv' ? field.label_lv : field.label_en}
            </dt>
            <dd className="text-sm text-[#121212] sm:col-span-2 break-words">
              {display}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
